import { useMutation, useQueryClient } from '@tanstack/react-query';
import { API, apiClient } from '../api_clients';
import { useAuthStore } from '../stores/auth-store';
import { useUserStore } from '../stores/user-store';
import { useInvitationAcceptStore } from '../stores/invitation-store';
import { extractErrorMessage, REFRESH_TOKEN_KEY, TOKEN_EXPIRES_AT_KEY, TOKEN_KEY } from './axiosIntance';
import { mapApiUserToEntity } from '../mappers/api-to-entity';
import { toast } from 'sonner';
import { useOrganizationStore } from '../stores/organization-store';
import { setCookie, eraseCookie } from '../utils/cookies';

// Re-export commonly used auth types from API
export type LoginResponse = API.ResponsesLoginResponseData;
export type RefreshTokenRequest = API.RefreshTokenRequest;
export type RefreshTokenResponse = API.ResponsesRefreshTokenResponseData;
export type RegisterRequest = API.SignUpWithEmailRequest
export type SignUpResponse = API.ResponsesOTPChallengeResponseData;
export type OTPChallengeResponse = API.ResponsesOTPChallengeResponseData;
export type InitiatePhoneSignInRequest = API.InitiatePhoneSignInRequest;
export type VerifyEmailSignInRequest = API.VerifyEmailSignInRequest;
export type VerifyPhoneSignInRequest = API.VerifyPhoneSignInRequest;

async function register(data: RegisterRequest): Promise<SignUpResponse> {
  const response = await apiClient.authApi.signUpWithEmail({
    email: data.email,
    name: data.name,
  });
  return response.data.data;
}

// OTP API functions
async function initiateEmailOTP(email: string): Promise<OTPChallengeResponse> {
  const response = await apiClient.authApi.initiateEmailSignIn({ email });
  return response.data.data;
}

async function verifyEmailOTP(data: VerifyEmailSignInRequest): Promise<LoginResponse> {
  const response = await apiClient.authApi.verifyEmailSignIn(data);
  return response.data.data;
}

async function initiatePhoneOTP(phone: string): Promise<OTPChallengeResponse> {
  const response = await apiClient.authApi.initiatePhoneSignIn({ phone });
  return response.data.data;
}

async function verifyPhoneOTP(data: VerifyPhoneSignInRequest): Promise<LoginResponse> {
  const response = await apiClient.authApi.verifyPhoneSignIn(data);
  return response.data.data;
}

async function refreshToken(data: API.RefreshTokenRequest): Promise<API.ResponsesRefreshTokenResponseData> {
  const response = await apiClient.authApi.refreshToken(data);
  return response.data.data;
}


// React Query hooks

export function useInitiateEmailOTP() {
  return useMutation({
    mutationFn: (email: string) => initiateEmailOTP(email),
    onError: (error) => {
      const errorMessage = extractErrorMessage(error);
      toast.error('Failed to send OTP', {
        description: errorMessage,
      });
    },
  });
}

export function useVerifyEmailOTP() {
  const { setAuthResponse } = useAuthStore();
  const { setUser } = useUserStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: VerifyEmailSignInRequest) => verifyEmailOTP(data),
    onSuccess: (data) => {
      if (data.auth.accessToken) {
        localStorage.setItem(TOKEN_KEY, data.auth.accessToken);
        setCookie(TOKEN_KEY, data.auth.accessToken, 7);
      }
      if (data.auth.refreshToken) {
        localStorage.setItem(REFRESH_TOKEN_KEY, data.auth.refreshToken);
        setCookie(REFRESH_TOKEN_KEY, data.auth.refreshToken, 7);
      }
      if (data.auth.expiresIn) {
        const expiresAtMs = Date.now() + (data.auth.expiresIn * 1000);
        localStorage.setItem(TOKEN_EXPIRES_AT_KEY, expiresAtMs.toString());
      }
      setAuthResponse({
        accessToken: data.auth.accessToken || '',
        refreshToken: data.auth.refreshToken || '',
        expiresAt: data.auth.expiresIn?.toString() || '',
      });
      const user = mapApiUserToEntity(data.user);
      setUser(user);
      queryClient.invalidateQueries();
    },
    onError: (error) => {
      const errorMessage = extractErrorMessage(error);
      toast.error('OTP verification failed', {
        description: errorMessage,
      });
    },
  });
}

export function useInitiatePhoneOTP() {
  return useMutation({
    mutationFn: (phone: string) => initiatePhoneOTP(phone),
    onError: (error) => {
      const errorMessage = extractErrorMessage(error);
      toast.error('Failed to send OTP', {
        description: errorMessage,
      });
    },
  });
}

export function useVerifyPhoneOTP() {
  const { setAuthResponse } = useAuthStore();
  const { setUser } = useUserStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: VerifyPhoneSignInRequest) => verifyPhoneOTP(data),
    onSuccess: (data) => {
      if (data.auth.accessToken) {
        localStorage.setItem(TOKEN_KEY, data.auth.accessToken);
        setCookie(TOKEN_KEY, data.auth.accessToken, 7);
      }
      if (data.auth.refreshToken) {
        localStorage.setItem(REFRESH_TOKEN_KEY, data.auth.refreshToken);
        setCookie(REFRESH_TOKEN_KEY, data.auth.refreshToken, 7);
      }
      if (data.auth.expiresIn) {
        const expiresAtMs = Date.now() + (data.auth.expiresIn * 1000);
        localStorage.setItem(TOKEN_EXPIRES_AT_KEY, expiresAtMs.toString());
      }
      setAuthResponse({
        accessToken: data.auth.accessToken || '',
        refreshToken: data.auth.refreshToken || '',
        expiresAt: data.auth.expiresIn?.toString() || '',
      });
      const user = mapApiUserToEntity(data.user);
      setUser(user);
      queryClient.invalidateQueries();
    },
    onError: (error) => {
      const errorMessage = extractErrorMessage(error);
      toast.error('OTP verification failed', {
        description: errorMessage,
      });
    },
  });
}

export function useRegister() {
  return useMutation({
    mutationFn: register,
    onSuccess: () => {
      toast.success('Verification code sent to your email!');
    },
    onError: (error) => {
      const errorMessage = extractErrorMessage(error);
      toast.error('Signup error', {
        description: errorMessage,
      });
    },
  });
}

export function useRefreshToken() {
  return useMutation({
    mutationFn: (data: API.RefreshTokenRequest) => refreshToken(data),
    onSuccess: (data) => {
      // Update tokens from auth object
      if (data.auth.accessToken) {
        localStorage.setItem(TOKEN_KEY, data.auth.accessToken);
      }
      if (data.auth.refreshToken) {
        localStorage.setItem(REFRESH_TOKEN_KEY, data.auth.refreshToken);
      }

      // Calculate and save expiration timestamp from expiresIn (seconds)
      if (data.auth.expiresIn) {
        const expiresAtMs = Date.now() + (data.auth.expiresIn * 1000);
        localStorage.setItem(TOKEN_EXPIRES_AT_KEY, expiresAtMs.toString());
      }
    },
  });
}

export function useLogout() {
  const { clearAuth } = useAuthStore();
  const { clearUser } = useUserStore();
  const { clearOrganization, clearOrganizations } = useOrganizationStore();
  const { clearInvitation } = useInvitationAcceptStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      // Call logout endpoint if available
      // await apiClient.authApi.logout();
      return true
    },
    onSuccess: () => {
      // Clear tokens from localStorage
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      localStorage.clear()

      // Clear cookies for middleware
      eraseCookie(TOKEN_KEY);
      eraseCookie(REFRESH_TOKEN_KEY);

      // Clear auth store (tokens)
      clearAuth();

      // Clear user store
      clearUser();

      // Clear organization store
      clearOrganization();
      clearOrganizations();

      // Clear invitation store
      clearInvitation();

      // Clear all queries
      queryClient.clear();
    },
    onError: (error) => {
      const errorMessage = extractErrorMessage(error);
      toast.error('Logout error', {
        description: errorMessage,
      });
    },
  });
}


// Verify email with code
async function verifyEmail(data: API.ConfirmSignUpRequest): Promise<void> {
  await apiClient.authApi.confirmSignUp(data);
}

export function useVerifyEmail() {
  return useMutation({
    mutationFn: verifyEmail,
    onSuccess: () => {
      toast.success('Email verified successfully!');
    },
    onError: (error) => {
      const errorMessage = extractErrorMessage(error);
      toast.error('Verification failed', {
        description: errorMessage,
      });
    },
  });
}

// Resend verification code
async function resendVerification(data: API.ResendConfirmationCodeRequest): Promise<void> {
  await apiClient.authApi.resendConfirmationCode(data);
}

export function useResendVerification() {
  return useMutation({
    mutationFn: resendVerification,
    onSuccess: () => {
      toast.success('Verification code sent!');
    },
    onError: (error) => {
      const errorMessage = extractErrorMessage(error);
      toast.error('Failed to resend code', {
        description: errorMessage,
      });
    },
  });
}
